import React, { useContext } from 'react';
import type { RenderFn } from '@atlassian/forge-ui-types';
import type { ChartColorTokens } from '../../utils/colorTokens/chartColorTokens';
import { getChartColorTokenValues } from '../../utils/getColorTokenValue';
import { RendererContext } from '../../../context';

const PlatformPieChart = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_piechart' */
		'@atlassian/visualization/charts'
	).then((module) => ({ default: module.PieChart })),
);

const Title = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_piechart' */
		'@atlassian/visualization/charts'
	).then((module) => ({ default: module.PieChart.elements.Title })),
);

const SubTitle = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_piechart' */
		'@atlassian/visualization/charts'
	).then((module) => ({ default: module.PieChart.elements.SubTitle })),
);

const MarkLabels = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_piechart' */
		'@atlassian/visualization/charts'
	).then((module) => ({ default: module.PieChart.elements.MarkLabels })),
);

type PlatformPieChartProps = React.ComponentProps<typeof PlatformPieChart>;

/**
 * Excluded props:
 * - grid - does not work across all components
 * - showTableOption - does not work across all components and will be moved into a primitive
 * - showToolTip - does not work across all components
 * - children - we will not be supporting primitives/elements to compose the chart
 * - patterns
 * - isPatterned
 * - sort
 * - innerRadius - doesn't work well right now since the donut center label cant be removed
 *
 * Limited props:
 * - xAccessor - only allow non function types
 * - yAccessor - only allow non function types
 * - colorAccessor - only allow non function types
 * - colors - only allow chart color tokens (color.chart.*)
 */
export type PieChartProps = Pick<
	PlatformPieChartProps,
	'width' | 'height' | 'data' | 'showBorder' | 'isDonut'
> & {
	colorAccessor: number | string;
	labelAccessor: number | string;
	valueAccessor: number | string;
	title?: string;
	subtitle?: string;
	showMarkLabels?: boolean;
	colors?: ChartColorTokens[];
};

export const PieChart = (props: Parameters<RenderFn>[0]) => {
	const {
		width,
		height,
		data,
		showBorder,
		colorAccessor,
		labelAccessor,
		valueAccessor,
		colors,
		title,
		subtitle,
		isDonut,
		showMarkLabels,
	} = props.forgeDoc.props as PieChartProps;

	const colorValues = getChartColorTokenValues(colors);

	// Stop viz components from being rendered in Prod Forge environment
	const { forgeEnvironment } = useContext(RendererContext);
	if (forgeEnvironment === 'PRODUCTION') {
		throw new Error(
			'EAP features are only accessible to users with EAP signup and not supported or recommended for use in production.',
		);
	}

	return (
		<PlatformPieChart
			width={width}
			height={height ? height : 400}
			data={data}
			showBorder={showBorder}
			colorAccessor={colorAccessor}
			valueAccessor={valueAccessor}
			labelAccessor={labelAccessor}
			colors={colorValues}
			isDonut={isDonut}
		>
			<MarkLabels hide={!showMarkLabels} />
			<MarkLabels hide={!showMarkLabels} />
			{title && <Title>{title}</Title>}
			{subtitle && <SubTitle>{subtitle}</SubTitle>}
		</PlatformPieChart>
	);
};
